import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import GradingIcon from "@mui/icons-material/Grading";
import { PrimaryButton } from "../../components/others/ButtonPrimary";
import LavorisService from "../../services/LavorisService";
import { RootState } from "../../store/combineReducer";
import { isPortalAccessBlocked } from "../../hooks/useSyncMockBillingPolicy";
import { getTokens } from "../../utils/auth/getTokensLocalStorage";

const TERMS_AND_CONDITIONS_URL =
  "https://drive.google.com/file/d/1QsNMjvrop_U-3G4_jzjxxR7UF3DsX3-q/view?usp=sharing";

const formatCatalogValue = (value: string | undefined): string => {
  if (!value) return "N/A";
  return value.split("_").join(" ").toLocaleLowerCase();
};

const BillingConditionsTab = () => {
  const dispatch = useDispatch();
  const {
    userId,
    currentRole,
    currentCompany,
    companies,
    portalTermsAccepted,
    billingPolicyLatest,
  } = useSelector((state: RootState) => state.user);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  const billingData = billingPolicyLatest;
  const isCompanyAdmin = currentRole === "COMPANY_ADMIN";
  const showAcceptButton =
    isCompanyAdmin && isPortalAccessBlocked(portalTermsAccepted);

  const isAccepted = !isPortalAccessBlocked(portalTermsAccepted);

  const getIntroText = (): string => {
    if (!isAccepted) {
      return "Para acceder al portal de clientes, el Administrador de la Empresa debe revisar las condiciones de facturación y políticas operativas de Lavoris.";
    }

    if (isCompanyAdmin) {
      return "Aceptaste las condiciones de facturación y políticas operativas. Tu empresa ya tiene acceso a todos los módulos del portal. A continuación podés consultar los datos de facturación vigentes.";
    }

    return "Las condiciones de facturación y políticas operativas fueron aceptadas por el Administrador de la Empresa. A continuación podés consultar los datos de facturación vigentes.";
  };

  const handleAccept = async () => {
    setIsLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const companyId = currentCompany?.id ?? companies?.[0]?.id;
      if (!companyId || !userId) {
        setError("No se pudo identificar la empresa o el usuario.");
        return;
      }

      const LavorisInstance = LavorisService.getInstance();
      await LavorisInstance.RolService.updateAdminBillingCondition({
        userId: userId,
        companyId: companyId,
        billing_policy_aproved: true,
      });

      const tokens = getTokens();
      if (tokens?.access_token) {
        await LavorisInstance.User.getProfile(tokens, currentCompany);
      }

      setSuccessMessage(
        "Listo. A partir de este momento vas a poder hacer uso completo del sistema.",
      );
    } catch (e: any) {
      setError(
        e?.response?.data?.message ||
        "No se pudieron aceptar las condiciones de facturación. Intente nuevamente.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="gestiones-tab-content">
      <p className="gestiones-tab-description">{getIntroText()}</p>

      <div className="gestiones-billing-section">
        <h6 className="gestiones-billing-title">Datos de facturación vigente</h6>
        <div className="gestiones-billing-fields">
          <div className="gestiones-billing-field">
            <label>Inicio del ciclo</label>
            <span>
              {billingData?.billingStartDate ?? "N/A"}
            </span>
          </div>
          <div className="gestiones-billing-field">
            <label>Ciclo de facturación</label>
            <span>{formatCatalogValue(billingData?.invoiceDate)}</span>
          </div>
          <div className="gestiones-billing-field">
            <label>Plazo de pago</label>
            <span>{formatCatalogValue(billingData?.paymentDeadline)}</span>
          </div>
        </div>
      </div>
      <div className="gestiones-terms-footer">
        <GradingIcon className="text-[#fb544f]" />
        <a
          href={TERMS_AND_CONDITIONS_URL}
          target="_blank"
          rel="noopener noreferrer"
          title="Términos y condiciones"
          className="underline text-[#fb544f]"
        >
          Conocé nuestros Términos y Condiciones
        </a>
      </div>

      {showAcceptButton && (
        <div className="gestiones-accept-section">
          <p className="gestiones-accept-notice">
            Al aceptar, se habilitará el acceso a todos los módulos del portal
            para su empresa.
          </p>
          <PrimaryButton
            type="button"
            text="Aceptar condiciones de facturación"
            onClick={handleAccept}
            disabled={isLoading}
            loading={isLoading}
            styles="gestiones-accept-button"
          />
          {successMessage && (
            <small className="gestiones-success">{successMessage}</small>
          )}
          {error && <small className="gestiones-error">{error}</small>}
        </div>
      )}

    </div>
  );
};

export default BillingConditionsTab;
